import clsx from 'clsx';
import { FormViewType } from '@kinetic-ui/data-layer';
import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationFormContext } from '../context';
import { useLocationDetailsForm } from './hooks';
import useLocationFormDetailStyles from './styles';
import { DETAILS } from './const';
import { Grid } from '@mui/material';
import { LocationPointMap } from './components/LocationPointMap';
import { LocationPolygonMap } from './components/LocationPolygonMap';
import { usePolygonGeometryState } from './hooks/usePolygonGeometryState';
import { usePointGeometryState } from './hooks/usePointGeometryState';
import { useSegmentGeometryState } from './hooks/useSegmentGeometryState';
import { LocationSegmentMap } from './components/LocationSegmentMap';
import { useIsLocationTypePoint } from '../hooks/useIsLocationTypePoint';
import { useIsLocationTypePolygon } from '../hooks/useIsLocationTypePolygon';
import { useIsLocationTypeSegment } from '../hooks/useIsLocationTypeSegment';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { KineticForm, renderView, ViewOptions } from 'components/forms';
import { useRouteBuilder } from 'kineticUtils';
import { LOCATIONS_PAGE_ROUTE } from 'const';
import { useDynamicDocumentationLink } from 'components/layouts';

const LocationDetails: React.FunctionComponent = () => {
  const { t } = useTranslation(['locations', 'common', 'maps']);
  const classes = useLocationFormDetailStyles();
  const route = useRouteBuilder(LOCATIONS_PAGE_ROUTE);

  useDynamicDocumentationLink({ link: 'LOCATION_EDIT_DETAILS' });

  const {
    values: locationFormValues,
    setValues: setLocationFormValues,
    setDirty,
    setError,
    disabled,
    dirty,
    getGeometricConfigEnabled,
  } = useContext(LocationFormContext);

  const {
    formProps,
    values: [values],
    isDirty,
    errorMessages,
    setDirty: setDirtyFields,
  } = useLocationDetailsForm(locationFormValues, !disabled);

  const locationType = locationFormValues?.locationType;
  const { isLocationTypePoint, loading } = useIsLocationTypePoint(locationType);
  const { isLocationTypePolygon } = useIsLocationTypePolygon(locationType);
  const { isLocationTypeSegment } = useIsLocationTypeSegment(locationType);

  useEffect(() => {
    if (!Object.keys(dirty)?.length) {
      setDirtyFields([]);
    }
  }, [dirty, setDirtyFields]);

  useEffect(() => {
    if (isDirty) {
      setDirty(DETAILS, isDirty);
    }
  }, [setDirty, isDirty]);

  useEffect(() => {
    setError(DETAILS, errorMessages);
  }, [setError, errorMessages]);

  useEffect(() => {
    if (values) {
      setLocationFormValues((prev) => {
        const geometricConfigDisabled = !getGeometricConfigEnabled(values.locationType);
        const locationTypeChanged = prev.locationType !== values.locationType;
        const updatedValues = { ...prev, ...values };

        if (locationTypeChanged && geometricConfigDisabled) {
          return {
            ...updatedValues,
            geometry: undefined,
            crosswalks: undefined,
            maneuvers: undefined,
            lanes: undefined,
          };
        }

        if (locationTypeChanged) {
          return {
            ...updatedValues,
            geometry: undefined,
          };
        }

        return { ...updatedValues };
      });
    }
  }, [setLocationFormValues, getGeometricConfigEnabled, values]);

  const {
    onPinDrop,
    isPinActive,
    setPinActive,
    sectionOptions: pointGeometrySection,
  } = usePointGeometryState();

  const {
    polygonColor,
    polygonOpacity,
    sectionOptions: polygonGeometrySection,
  } = usePolygonGeometryState(disabled);

  const {
    segmentColor,
    segmentOpacity,
    sectionOptions: segmentGeometrySection,
  } = useSegmentGeometryState(disabled);

  const viewProps: ViewOptions = useMemo(() => {
    const sectionsOptions = [
      {
        additionalCardClasses: classes.details,
        fieldNames: ['name', 'description'],
        hashRoute: 'locationDetails',
        sectionId: 'locationDetails',
        name: t('locationDetails'),
      },
      {
        fieldNames: ['locationType'],
        name: t('locationType'),
        hashRoute: 'locationType',
        sectionId: 'locationType',
      },
    ];
    isLocationTypeSegment && sectionsOptions.push(segmentGeometrySection);
    isLocationTypePolygon && sectionsOptions.push(polygonGeometrySection);
    isLocationTypePoint && sectionsOptions.push(pointGeometrySection);
    return {
      type: FormViewType.Embedded,
      title: t('location'),
      viewId: 'details',
      sectionsOptions,
      paths: [],
    };
  }, [
    classes.details,
    isLocationTypePoint,
    isLocationTypePolygon,
    isLocationTypeSegment,
    pointGeometrySection,
    polygonGeometrySection,
    segmentGeometrySection,
    t,
  ]);

  if (loading) {
    return <LoadingSkeleton items={20} />;
  }

  return (
    <KineticForm {...formProps}>
      {({ fieldComponents }) => (
        <Grid className={classes.fullHeight} container spacing={2}>
          <Grid className={classes.fullHeight} item xs={12} xl={5}>
            {isLocationTypePoint && (
              <LocationPointMap
                setPinActive={setPinActive}
                isPinActive={isPinActive}
                onPinDrop={onPinDrop}
              />
            )}
            {isLocationTypeSegment && (
              <LocationSegmentMap
                segmentOpacity={segmentOpacity}
                segmentColor={segmentColor}
                key='locationSegmentMap'
              />
            )}
            {isLocationTypePolygon && (
              <LocationPolygonMap
                polygonOpacity={polygonOpacity}
                polygonColor={polygonColor}
                key='locationPolygonMap'
              />
            )}
          </Grid>
          <Grid className={clsx(classes.gridContainer, classes.detailSection)} item xs={12} xl={7}>
            {renderView(viewProps, fieldComponents, route)}
          </Grid>
        </Grid>
      )}
    </KineticForm>
  );
};

export default LocationDetails;
